import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FiArrowLeft } from 'react-icons/fi';
import DashboardShell from './DashboardShell';

interface TabDef {
  key: string;
  label: string;
  content: React.ReactNode;
}

interface DashboardDetailLayoutProps {
  title: string;
  subtitle?: string;
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  tabs: TabDef[];
  defaultTab?: string;
  sidebar?: React.ReactNode;
  backPath: string;
}

const DashboardDetailLayout: React.FC<DashboardDetailLayoutProps> = ({
  title,
  subtitle,
  badges,
  actions,
  tabs,
  defaultTab,
  sidebar,
  backPath,
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.key);

  const activeContent = tabs.find(t => t.key === activeTab)?.content;

  return (
    <DashboardShell title={title}>
      {(dateRange) => (
        <div>
          {/* Back */}
          <button
            onClick={() => navigate(backPath)}
            className={`flex items-center gap-1.5 text-sm mb-4 px-2 py-1 rounded-lg transition-all duration-200 ${
              darkMode
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FiArrowLeft className="w-4 h-4" /> Back
          </button>

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">{title}</h2>
                {badges}
              </div>
              {subtitle && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>

          {/* Tab bar */}
          <div className={`flex border-b mb-6 overflow-x-auto ${darkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'border-purple-500 text-purple-400 bg-purple-500/5'
                    : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content + sidebar */}
          <div className={sidebar ? 'flex gap-6' : ''}>
            <div className={sidebar ? 'flex-1 min-w-0' : ''}>
              {activeContent}
            </div>
            {sidebar && (
              <div className="w-80 shrink-0">{sidebar}</div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default DashboardDetailLayout;
